import {
  ApplicationConfig,
  importProvidersFrom,
  isDevMode,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withPreloading, withRouterConfig } from '@angular/router';

import { registerLocaleData } from '@angular/common';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import en from '@angular/common/locales/en';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideServiceWorker } from '@angular/service-worker';
import { ApiModule } from '@buspro/web/apis';
import {
  authTokenInterceptor,
  httpErrorInterceptor,
  LocalAuthService,
  SelectivePreloadingStrategy,
  TANSTACK_SENSIBLE_DEFAULTS,
  tenantImpersonationInterceptor,
  tokenRefreshInterceptor,
  WEB_CONFIG,
} from '@buspro/web/core';
import { provideLoadingBarInterceptor } from '@ngx-loading-bar/http-client';
import { provideLoadingBarRouter } from '@ngx-loading-bar/router';
import {
  QueryClient,
  provideTanStackQuery,
} from '@tanstack/angular-query-experimental';
import {
  AuthenticationProvider,
  LocalStorageProvider,
  provideNgxAuth,
  StorageProvider,
} from '@corelabs/ng-auth';
import { en_US, provideNzI18n } from 'ng-zorro-antd/i18n';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationModule } from 'ng-zorro-antd/notification';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { NgxPermissionsModule } from 'ngx-permissions';
import { environment } from '../environments/environment';
import { routes } from './admin.routes';

registerLocaleData(en);
export const adminConfig: ApplicationConfig = {
  providers: [
    // Auth providers must be provided BEFORE router and HTTP client
    // because guards and interceptors depend on them
    provideNgxAuth({
      authenticationProvider: {
        provide: AuthenticationProvider,
        useClass: LocalAuthService,
        deps: [],
      },
      storageProvider: {
        provide: StorageProvider,
        useClass: LocalStorageProvider,
      },
      homeUrl: '/dashboard',
      noAuthRedirectUrl: '/passport/login',
      sessionExpiredRedirectUrl: '/passport/login',
      authenticationHeader: 'Authorization',
      tokenType: 'Bearer',
    }),
    provideClientHydration(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(
      withFetch(),
      // Order matters (#852): errors travel right-to-left, so the error
      // interceptor is outermost and sees only 401s the refresh could not
      // rescue. Impersonation stays innermost so a retried request keeps it.
      withInterceptors([httpErrorInterceptor, authTokenInterceptor, tokenRefreshInterceptor, tenantImpersonationInterceptor]),
      withInterceptorsFromDi()
    ),
    provideTanStackQuery(new QueryClient({
      defaultOptions: {
        ...TANSTACK_SENSIBLE_DEFAULTS,
      }
    })),
    provideRouter(
      routes,
      withPreloading(SelectivePreloadingStrategy),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
        onSameUrlNavigation: 'reload',
      }),
      // withDebugTracing()
    ),
    // Single animations engine, consolidated from a duplicate registration (provideAnimationsAsync()
    // was previously listed twice in this providers array — a config bug). Uses the synchronous
    // provider to match the tenant/website apps. (Not the cause of the terminals freeze — that was
    // a change-detection loop in the terminals component — but registering a provider twice is wrong.)
    provideAnimations(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    // Dynamic icon loading is enabled via assets configuration in project.json
    // No need to provide static icons - they will be loaded on demand
    provideNzI18n(en_US),
    provideLoadingBarInterceptor(),
    provideLoadingBarRouter(),
    // provideLoadingBar({ latencyThreshold: 100 }),
    importProvidersFrom([
      ApiModule.forRoot({
        rootUrl: environment.server,
      }),
      NzNotificationModule,
      NzModalModule,
      NgxPermissionsModule.forRoot(),
      LoggerModule.forRoot({
        level: NgxLoggerLevel.INFO,
        serverLogLevel: NgxLoggerLevel.ERROR,
      }),
    ]),
    { provide: WEB_CONFIG, useValue: environment },
  ],
};
